import React, { useState, useMemo, useEffect, useRef } from 'react'

import { useParams, useNavigate, useLocation } from 'react-router-dom'

import {
	getTypeformUrl,
	KEY_RATE_PLAYLIST,
	KEY_APP_USER_FEEDBACK,
	KEY_EDIT_PLAYLIST,
} from '@/core/api/data/typeforms'
import { FeedbackType } from '@/core/api/entities/song/feedbackType'
import { Song } from '@/core/api/entities/song/song'
import { SongFeedback } from '@/core/api/entities/song/songFeedback'
import { playlistService } from '@/core/api/services/playlistService'
import {
	isAccessDeniedError,
	shouldRetryRequest,
} from '@/core/api/utils/httpErrors'
import { useFeedbackTypes } from '@/core/hooks/misc/useFeedbackTypes'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import {
	usePlaylistById,
	usePlaylistFeedback,
	useSubmitSongFeedback,
} from '@/core/hooks/usePlaylist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import PlaylistDetailsPageUI from './PlaylistDetailsPageUI'

const PlaylistDetailsPage: React.FC = () => {
	const { playlistId } = useParams<{ playlistId: string }>()
	const navigate = useNavigate()
	const location = useLocation()
	const { currentLanguage, t } = useTranslation('common')
	const [accessDeniedModalOpen, setAccessDeniedModalOpen] = useState(false)
	const redirectToRef = useRef<string>('/music')
	const { showNotification } = useNotificationContext()

	// Ensure the page starts at the top whenever a playlist detail is opened
	useEffect(() => {
		window.scrollTo({ top: 0, left: 0 })
	}, [playlistId])

	// Get favorite status from navigation state
	const initialFavoriteState =
		(location.state as { isFavorite?: boolean })?.isFavorite ?? false
	const [searchQuery, setSearchQuery] = useState('')
	const [statusFilter, setStatusFilter] = useState<'all' | 'disliked'>('all')
	const [songFeedbacks, setSongFeedbacks] = useState<Map<number, SongFeedback>>(
		new Map()
	)
	const [showRateModal, setShowRateModal] = useState(false)
	const [showDislikeFeedbackModal, setShowDislikeFeedbackModal] =
		useState(false)
	const [showFeedbackDropModal, setShowFeedbackDropModal] = useState(false)
	const [showRequestModificationsModal, setShowRequestModificationsModal] =
		useState(false)
	const [showAssignPlaylistModal, setShowAssignPlaylistModal] = useState(false)
	const [
		showNegativeFeedbackOptionsModal,
		setShowNegativeFeedbackOptionsModal,
	] = useState(false)
	const [canRequestModifications, setCanRequestModifications] = useState(false)
	const [selectedSongForFeedback, setSelectedSongForFeedback] = useState<
		number | null
	>(null)
	const [feedbackDropRatio, setFeedbackDropRatio] = useState<number | null>(
		null
	)
	const [isApproving, setIsApproving] = useState(false)
	const feedbackTypesRef = useRef<FeedbackType[]>([])

	// Get account data for account_type
	const currentAccount = useCurrentAccount()

	const accountType = currentAccount?.accountType || 'brandtrack'

	useEffect(() => {
		if (currentAccount) {
			setCanRequestModifications(
				(currentAccount.subscriptions?.filter(
					(subscription) => subscription.plan?.metadata?.canRequestPlaylists
				).length || 0) > 0
			)
		}
	}, [currentAccount])

	// Fetch playlist details
	const {
		data: playlist,
		isLoading,
		error,
	} = usePlaylistById({
		playlistId: parseInt(playlistId || '0'),
		config: useAccountConfig({
			params: {
				include: 'songs,highlighted_songs',
			},
		}),
		options: {
			enabled: !!playlistId,
			retry: shouldRetryRequest,
		},
	})

	// Warn before redirecting on 403/404 errors (more user-friendly)
	useEffect(() => {
		if (!error) return
		if (!isAccessDeniedError(error)) return

		redirectToRef.current = '/music'
		setAccessDeniedModalOpen(true)
	}, [error])

	useEffect(() => {
		if (playlist) {
			setFeedbackDropRatio(playlist.feedbackDropRatio || 0)
		}
	}, [playlist])

	// Fetch feedback types
	const { data: feedbackTypes } = useFeedbackTypes()

	const {
		data: playlistFeedbacks,
		isLoading: isLoadingPlaylistFeedbacks,
		refetch: refetchPlaylistFeedbacks,
	} = usePlaylistFeedback({
		playlistId: playlist?.id,
		config: useAccountConfig({}),
		options: {
			enabled: !!playlist?.id,
		},
	})

	useEffect(() => {
		if (playlistFeedbacks) {
			setSongFeedbacks(
				new Map(
					playlistFeedbacks.map((feedback) => [feedback.songId, feedback])
				)
			)
		}
	}, [playlistFeedbacks])

	// Filter songs based on search query and status filter
	const filteredSongs = useMemo(() => {
		if (!playlist?.songs) return []

		let songs = playlist.songs

		// Apply status filter (disliked songs)
		if (statusFilter === 'disliked') {
			songs = songs.filter((song: Song) => {
				const songFeedback = songFeedbacks.get(song.id)
				const feedbackType = songFeedback?.feedbackType
				// Check if song has negative feedback using is_negative flag
				return feedbackType?.isNegative
			})
		}

		// Apply search query filter
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase()
			songs = songs.filter((song: Song) => {
				const title = song.metadata.title?.toLowerCase() || ''
				const artist = song.metadata.artist?.toLowerCase() || ''
				return title.includes(query) || artist.includes(query)
			})
		}

		// Apply highlighted ordering: highlighted songs first (in API order), then the rest
		const highlightedSongs = playlist.highlightedSongs
		if (highlightedSongs && highlightedSongs.length > 0) {
			const filteredSet = new Set(songs.map((s: Song) => s.id))
			const orderedHighlighted = highlightedSongs.filter((s: Song) =>
				filteredSet.has(s.id)
			)
			const highlightedIds = new Set(orderedHighlighted.map((s: Song) => s.id))
			const remaining = songs.filter((s: Song) => !highlightedIds.has(s.id))
			songs = [...orderedHighlighted, ...remaining]
		}

		return songs
	}, [
		playlist?.songs,
		playlist?.highlightedSongs,
		searchQuery,
		statusFilter,
		songFeedbacks,
	])

	// Normalize language code (e.g., 'en-US' -> 'en')
	const normalizedLanguage = useMemo(() => {
		return currentLanguage?.split('-')[0] || 'en'
	}, [currentLanguage])

	// Get typeform URL for rate playlist - recompute when dependencies change
	const ratePlaylistUrl = useMemo(() => {
		if (!accountType || !normalizedLanguage) {
			return null
		}
		return getTypeformUrl(KEY_RATE_PLAYLIST, normalizedLanguage, accountType, {
			playlist: playlist?.id,
		})
	}, [normalizedLanguage, accountType, playlist?.id])

	// Get typeform URL for dislike feedback - recompute when dependencies change
	const dislikeFeedbackUrl = useMemo(() => {
		if (!accountType || !normalizedLanguage) {
			return null
		}
		return getTypeformUrl(
			KEY_APP_USER_FEEDBACK,
			normalizedLanguage,
			accountType,
			{
				playlist: playlist?.id,
			}
		)
	}, [normalizedLanguage, accountType, playlist?.id])

	// Get typeform URL for request modifications (edit playlist) - recompute when dependencies change
	const requestModificationsUrl = useMemo(() => {
		if (!currentAccount || !normalizedLanguage) {
			return null
		}
		return getTypeformUrl(KEY_EDIT_PLAYLIST, normalizedLanguage, 'brandtrack', {
			playlist: playlist?.id,
		})
	}, [normalizedLanguage, currentAccount, playlist?.id])

	const handleBack = () => {
		navigate(-1)
	}

	const handleRateClick = () => {
		if (ratePlaylistUrl) {
			setShowRateModal(true)
		} else {
			console.warn('ratePlaylistUrl is null or undefined')
		}
	}

	const handleCloseRateModal = () => {
		setShowRateModal(false)
	}

	const { mutate: submitSongFeedback, isPending: isSubmittingFeedback } =
		useSubmitSongFeedback({
			config: useAccountConfig({}),
			options: {
				onSuccess: (feedbackDropRatio?: number) => {
					showNotification({
						type: 'success',
						message: t('entities.music.feedback_success'),
						duration: 3,
					})
					setSelectedSongForFeedback(null)

					if (feedbackDropRatio !== undefined && feedbackDropRatio >= 0.3) {
						setFeedbackDropRatio(feedbackDropRatio)
						setShowFeedbackDropModal(true)
					}

					refetchPlaylistFeedbacks()
				},
			},
		})

	const handleSubmitFeedback = (songId: number, feedbackTypeId: number) => {
		if (!playlist?.id) return

		setSelectedSongForFeedback(songId)
		submitSongFeedback({
			playlistId: playlist?.id,
			songId: songId,
			data: {
				feedback_type_id: feedbackTypeId,
			},
		})
	}

	const handleCloseFeedbackDropModal = () => {
		setShowFeedbackDropModal(false)
	}

	const handleCloseNegativeFeedbackOptionsModal = () => {
		setShowNegativeFeedbackOptionsModal(false)
		setSelectedSongForFeedback(null)
	}

	const handleCloseDislikeFeedbackModal = () => {
		setShowDislikeFeedbackModal(false)
	}

	const handleRequestModificationsClick = () => {
		if (requestModificationsUrl) {
			setShowRequestModificationsModal(true)
		}
	}

	const handleCloseRequestModificationsModal = () => {
		setShowRequestModificationsModal(false)
	}

	const handleApprovePlaylist = async () => {
		if (!playlist?.id || isApproving) return

		setIsApproving(true)
		try {
			await playlistService.approvePlaylist(playlist.id)
			showNotification({
				message: t('entities.music.playlist_approved_success'),
				type: 'success',
				duration: 3,
			})
			// Wait 1.5 seconds before refreshing to show the success notification
			setTimeout(() => {
				window.location.reload() // Refresh to show the regular buttons since playlist is now reviewed
			}, 1500)
		} catch (error) {
			console.error('Error approving playlist:', error)
			showNotification({
				message: t('entities.music.playlist_approve_error'),
				type: 'error',
				duration: 3,
			})
			setIsApproving(false)
		}
	}

	if (error) {
		// For 403/404 we show a warning modal and redirect after user acknowledges.
		// For other errors, show the generic error.
		if (isAccessDeniedError(error)) {
			return (
				<Dialog
					open={true}
					onOpenChange={(open) => {
						if (!open) {
							navigate('/music', { replace: true })
						}
					}}
				>
					<DialogContent className="sm:max-w-[520px]">
						<DialogHeader>
							<DialogTitle>{t('playlists.accessDenied.title')}</DialogTitle>
							<DialogDescription>
								{t('playlists.accessDenied.description')}
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() =>
									window.open('https://wa.me/15557052438', '_blank')
								}
							>
								{t('actions.contactSupport')}
							</Button>
							<Button
								onClick={() => {
									setAccessDeniedModalOpen(false)
									navigate(redirectToRef.current, { replace: true })
								}}
							>
								{t('actions.close')}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)
		}

		return (
			<div>
				<p>{t('entities.music.error_loading_playlist')}</p>
			</div>
		)
	}

	return (
		<>
			<Dialog
				open={accessDeniedModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						setAccessDeniedModalOpen(false)
						navigate(redirectToRef.current, { replace: true })
						return
					}
					setAccessDeniedModalOpen(true)
				}}
			>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{t('playlists.accessDenied.title')}</DialogTitle>
						<DialogDescription>
							{t('playlists.accessDenied.description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							onClick={() => {
								setAccessDeniedModalOpen(false)
								navigate(redirectToRef.current, { replace: true })
							}}
						>
							{t('actions.close')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<PlaylistDetailsPageUI
				playlist={playlist}
				isLoading={isLoading}
				searchQuery={searchQuery}
				onSearchChange={setSearchQuery}
				filteredSongs={filteredSongs}
				onBack={handleBack}
				onRateClick={handleRateClick}
				ratePlaylistUrl={ratePlaylistUrl}
				songFeedbacks={songFeedbacks}
				isLoadingFeedback={isLoadingPlaylistFeedbacks}
				showRateModal={showRateModal}
				onCloseRateModal={handleCloseRateModal}
				statusFilter={statusFilter}
				onStatusFilterChange={setStatusFilter}
				onSubmitFeedback={handleSubmitFeedback}
				feedbackTypes={feedbackTypes}
				playlistFeedbacks={playlistFeedbacks}
				showFeedbackDropModal={showFeedbackDropModal}
				onCloseFeedbackDropModal={handleCloseFeedbackDropModal}
				feedbackDropRatio={feedbackDropRatio}
				setShowFeedbackDropModal={setShowFeedbackDropModal}
				showDislikeFeedbackModal={showDislikeFeedbackModal}
				onCloseDislikeFeedbackModal={handleCloseDislikeFeedbackModal}
				dislikeFeedbackUrl={dislikeFeedbackUrl}
				allFeedbackTypes={feedbackTypesRef.current}
				initialFavoriteState={initialFavoriteState}
				onRequestModificationsClick={handleRequestModificationsClick}
				requestModificationsUrl={requestModificationsUrl}
				showRequestModificationsModal={showRequestModificationsModal}
				onCloseRequestModificationsModal={handleCloseRequestModificationsModal}
				onApprovePlaylist={handleApprovePlaylist}
				isApproving={isApproving}
				showNegativeFeedbackOptionsModal={showNegativeFeedbackOptionsModal}
				setShowNegativeFeedbackOptionsModal={
					setShowNegativeFeedbackOptionsModal
				}
				onCloseNegativeFeedbackOptionsModal={
					handleCloseNegativeFeedbackOptionsModal
				}
				selectedSongForFeedback={selectedSongForFeedback}
				setSelectedSongForFeedback={setSelectedSongForFeedback}
				currentLanguage={currentLanguage}
				isSubmittingFeedback={isSubmittingFeedback}
				canRequestModifications={canRequestModifications}
				showAssignPlaylistModal={showAssignPlaylistModal}
				onAssignPlaylistModalChange={setShowAssignPlaylistModal}
				canAssignPlaylists
			/>
		</>
	)
}

export default PlaylistDetailsPage
